import { LensPayoutState, LensScoreFinalizationStatus } from "../../gql-generated/types";
import { type LensDetailsScoreWindowFragment } from "../api/queries/lensDetailsScores.gql-generated";
import {
    type LensPlusEarnings,
    type LensPlusPayoutRow,
    type LensScoreTotals,
    type PayoutPeriodAmount,
    ProgramPayoutStatus,
    type TopPerformerAward,
} from "../models";

import { toUtcMonthStart } from "./enrolledProgramFormat";

// Every lensScores read is guarded against partial or stale shapes.
type MaybeStale<T> = { readonly [K in keyof T]?: T[K] | null };
type RawWindow = MaybeStale<LensDetailsScoreWindowFragment> | null | undefined;
type RawPayout = MaybeStale<LensDetailsScoreWindowFragment["payout"]>;

interface ScoreWindow {
    month: string;
    isEstimated: boolean;
    totals: LensScoreTotals | null;
    payout: RawPayout | null;
}

interface PaidPeriod {
    month: string;
    amountMilliUsd: number;
    totals: LensScoreTotals | null;
}

// Partial so a state added server-side before this bundle knows it falls back to the payout record.
const STATUS_BY_PAYOUT_STATE: Partial<Record<LensPayoutState, ProgramPayoutStatus>> = {
    [LensPayoutState.Paid]: ProgramPayoutStatus.Paid,
    [LensPayoutState.Pending]: ProgramPayoutStatus.Pending,
    [LensPayoutState.NotDue]: ProgramPayoutStatus.Pending,
    [LensPayoutState.None]: ProgramPayoutStatus.None,
};

export const isAwaitingPayout = (status: ProgramPayoutStatus): boolean =>
    status === ProgramPayoutStatus.InProgress || status === ProgramPayoutStatus.Pending;

// Unparseable DateTimes would throw in toISOString and in every formatter downstream.
const toIsoDate = (value: string | null | undefined): string | null => {
    const time = value ? Date.parse(value) : Number.NaN;
    return Number.isNaN(time) ? null : new Date(time).toISOString();
};

const monthKey = (iso: string): string => iso.slice(0, 7);

// LCB's GetPayoutStats groups payout history by SubmittedAt (the upload month), one month after the month earned.
const toEarnedMonth = (period: string | null | undefined): string | null => {
    const iso = toIsoDate(period);
    return iso && toUtcMonthStart(new Date(iso), -1);
};

const byMonthDesc = <T extends { month: string }>(a: T, b: T): number => b.month.localeCompare(a.month);

const toTotals = (totals: MaybeStale<LensScoreTotals> | null | undefined): LensScoreTotals | null =>
    typeof totals?.lensScore === "number" &&
    typeof totals.subscriberActionsUu === "number" &&
    typeof totals.subscribeCount === "number"
        ? {
              lensScore: totals.lensScore,
              subscriberActionsUu: totals.subscriberActionsUu,
              subscribeCount: totals.subscribeCount,
          }
        : null;

const toScoreWindow = (window: RawWindow): ScoreWindow[] => {
    const month = toIsoDate(window?.windowStart);
    return window && month
        ? [
              {
                  month,
                  // Unknown statuses are treated as still changing.
                  isEstimated: window.finalizationStatus !== LensScoreFinalizationStatus.Finalized,
                  totals: toTotals(window.totals),
                  payout: window.payout?.state ? window.payout : null,
              },
          ]
        : [];
};

// The record's payout basis: users = score - 100 x signups. A paid month always scored, so a zero
// score means the record carries none (CSV era, or LCB not serving it yet) and stays unknown.
const toRecordTotals = ({
    payoutSubscriberActionsUu,
    payoutLensSubscribeCount,
    payoutLensScore,
}: PayoutPeriodAmount): LensScoreTotals | null => {
    const totals = toTotals({
        lensScore: payoutLensScore,
        subscriberActionsUu: payoutSubscriberActionsUu,
        subscribeCount: payoutLensSubscribeCount,
    });
    return totals && totals.lensScore > 0 ? totals : null;
};

// Stale cache entries can lack the date; an undated period can't be placed in a month.
const toPaidPeriods = (periods: readonly PayoutPeriodAmount[]): PaidPeriod[] =>
    periods
        .flatMap((period) => {
            const month = toEarnedMonth(period.period);
            return month
                ? [{ month, amountMilliUsd: Number(period.totalAmountMilliUsd ?? 0), totals: toRecordTotals(period) }]
                : [];
        })
        .sort(byMonthDesc);

const recordedAmount = (period: PaidPeriod | undefined): number | null =>
    period && period.amountMilliUsd > 0 ? period.amountMilliUsd : null;

const toPayoutRow = (
    month: string,
    window: ScoreWindow | undefined,
    period: PaidPeriod | undefined,
): LensPlusPayoutRow => {
    let status: ProgramPayoutStatus;
    let amountMilliUsd: number | null = null;

    if (window?.isEstimated) {
        status = ProgramPayoutStatus.InProgress;
    } else {
        const payoutState = window?.payout?.state;
        const scoreStatus = payoutState ? STATUS_BY_PAYOUT_STATE[payoutState] : undefined;
        const historyAmount = recordedAmount(period);

        // Scores confirm the status; a history record alone does not confirm payment.
        status = scoreStatus ?? (historyAmount === null ? ProgramPayoutStatus.None : ProgramPayoutStatus.Recorded);

        if (status !== ProgramPayoutStatus.None) {
            const scoreAmount = Number(window?.payout?.amountMilliUsd ?? 0);
            // Prefer the recorded amount, falling back to scores before a history record exists.
            amountMilliUsd = historyAmount ?? (scoreAmount > 0 ? scoreAmount : null);
        }
    }

    return {
        month,
        status,
        amountMilliUsd,
        payoutDate: toIsoDate(
            status === ProgramPayoutStatus.Paid ? window?.payout?.paidOn : window?.payout?.expectedBy,
        ),
        totals: window?.totals ?? period?.totals ?? null,
    };
};

interface LensPlusEarningsSources {
    scores: { selectedMonth?: RawWindow; priorClosedMonth?: RawWindow } | undefined;
    periods: readonly PayoutPeriodAmount[];
    totalEarnedMilliUsd: number;
    currentMonthStart: string;
}

export const buildLensPlusEarnings = ({
    scores,
    periods,
    totalEarnedMilliUsd,
    currentMonthStart,
}: LensPlusEarningsSources): LensPlusEarnings => {
    const selectedWindows = toScoreWindow(scores?.selectedMonth);
    const windows = [...selectedWindows, ...toScoreWindow(scores?.priorClosedMonth)];
    const [selectedMonth = { month: currentMonthStart, isEstimated: true, totals: null, payout: null }] =
        selectedWindows;
    const paidPeriods = toPaidPeriods(periods);
    const months = [...new Set([...windows, ...paidPeriods].map(({ month }) => monthKey(month)))];

    const payoutRows = months
        .map((key) => {
            const window = windows.find((w) => monthKey(w.month) === key);
            const period = paidPeriods.find((p) => monthKey(p.month) === key);
            return toPayoutRow(window?.month ?? period?.month ?? key, window, period);
        })
        .sort(byMonthDesc);

    const paid = paidPeriods.filter((p) => p.amountMilliUsd > 0);
    const paidTotal = paid.reduce((sum, p) => sum + p.amountMilliUsd, 0);
    const firstPaid = paid.at(-1);

    return {
        currentMonth: toPayoutRow(selectedMonth.month, selectedMonth, undefined),
        totalEarnedMilliUsd,
        // Only caption the lifetime total with history months that account for all of it.
        paidMonths:
            firstPaid && paidTotal === totalEarnedMilliUsd ? { count: paid.length, since: firstPaid.month } : null,
        payoutRows,
    };
};

export const buildTopPerformerAwards = (
    periods: readonly PayoutPeriodAmount[],
    qualifiedPosters: number,
    lastPayoutDate: string | null,
): TopPerformerAward[] => {
    const paid = toPaidPeriods(periods).filter((p) => p.amountMilliUsd > 0);
    // The reward record describes one award; attributing it to several would be a guess.
    const isSingleAward = paid.length === 1;
    return paid.map(({ month, amountMilliUsd }) => ({
        month,
        amountMilliUsd,
        qualifiedPosters: isSingleAward && qualifiedPosters > 0 ? qualifiedPosters : null,
        paidOn: isSingleAward ? toIsoDate(lastPayoutDate) : null,
    }));
};
